import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Button from "components/components/atoms/Button";
import useBreakpoint from "utils/hooks/useBreakpoint";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "tailwind.config.js";
import cx from "classnames";
import { useSpring, animated } from "react-spring";
import { useMeasure } from "react-use";
import trackingEvents from "utils/trackingEvents";
import SaleCenterCommunityCardPanelMobile from "components/components/molecules/SaleCenterCommunityCardPanelMobile";

const CardContainer = styled.div`
  margin-top: 0.313rem;
  margin-bottom: 0.25rem;
`;

const Card = styled.div`
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  min-height: 1.625rem;
  width: fit-content;
  min-width: 7.5rem;
`;

const CardTitle = styled.div`
  letter-spacing: -0.029rem;
`;

const Img = styled.img`
  min-height: 4.75rem;
  transition: transform 0.5s ease;
`;

const SaleCenterCommunityCardMobile = (props) => {
  const {
    city,
    state,
    communityName,
    direction,
    emailAddress,
    phone,
    saleCenterHours,
    zipCode,
    cardImage,
    flagText,
    firstCTAText,
    firstCTALink,
    secondCTAText,
    secondCTALink,
    openNow,
    latitude,
    longitude,
    salesCenterNoteVM,
    gmbLink = {},
    secondSalesCenters,
    title,
  } = props;
  const imageSrc =
    (cardImage && cardImage.srcMobile) || (cardImage && cardImage.src);

  const defaultHeight = "0px";
  const [contentCardHeight, setContentCardHeight] = useState(defaultHeight);
  const [cardOpen, toggleCardOpen] = useState(false);
  const [cardRefHeight, { height: cardHeight }] = useMeasure();
  const getProperCardHeight = () => {
    if (cardOpen) {
      return `${contentCardHeight}px`;
    }
    return "0px";
  };

  const expandCard = useSpring({
    height: getProperCardHeight(),
    pointerEvents: cardOpen ? "all" : "none",
  });
  const fadeCard = useSpring({
    opacity: cardOpen ? 1 : 0,
    delay: cardOpen ? 200 : 0,
  });
  const toggleCardSection = () => {
    toggleCardOpen(!cardOpen);
  };
  useEffect(() => {
    setContentCardHeight(cardHeight);
    window.addEventListener("resize", setContentCardHeight(cardHeight));

    return window.removeEventListener(
      "resize",
      setContentCardHeight(cardHeight)
    );
  }, [cardHeight]);
  const renderCardContent = () => (
    <animated.div
      className="flex flex-col mt-4  w-full"
      ref={cardRefHeight}
      style={fadeCard}
    >
      <SaleCenterCommunityCardPanelMobile
        city={city}
        state={state}
        direction={direction}
        emailAddress={emailAddress}
        phone={phone}
        saleCenterHours={saleCenterHours}
        zipCode={zipCode}
        firstCTAText={firstCTAText}
        firstCTALink={firstCTALink}
        secondCTAText={secondCTAText}
        secondCTALink={secondCTALink}
        openNow={openNow}
        latitude={latitude}
        longitude={longitude}
        salesCenterNoteVM={salesCenterNoteVM}
        gmbLink={gmbLink}
        title={title}
      />

      {secondSalesCenters && (
        <>
          <div className="h-4" />
          <SaleCenterCommunityCardPanelMobile
            city={secondSalesCenters.city}
            state={secondSalesCenters.state}
            direction={secondSalesCenters.direction}
            emailAddress={secondSalesCenters.emailAddress}
            phone={secondSalesCenters.phone}
            saleCenterHours={secondSalesCenters.saleCenterHours}
            zipCode={secondSalesCenters.zipCode}
            firstCTAText={secondSalesCenters.firstCTAText}
            firstCTALink={secondSalesCenters.firstCTALink}
            secondCTAText={secondSalesCenters.secondCTAText}
            secondCTALink={secondSalesCenters.secondCTALink}
            openNow={secondSalesCenters.openNow}
            latitude={secondSalesCenters.latitude}
            longitude={secondSalesCenters.longitude}
            salesCenterNoteVM={secondSalesCenters.salesCenterNoteVM}
            gmbLink={secondSalesCenters.gmbLink}
            title={secondSalesCenters.title}
          />
        </>
      )}
    </animated.div>
  );

  return (
    <CardContainer>
      <Card className="w-full bg-mattamy-white flex flex-col lg:flex-row justify-center items-start">
        <div className="flex flex-row w-full items-start">
          <div className="text-center relative rounded overflow-hidden w-3/12 h-16">
            <div className="overflow-hidden rounded">
              <Img
                src={imageSrc}
                alt={(cardImage && cardImage.alt) || ""}
                className={"bg-gray-300 object-cover w-full"}
              />
            </div>
          </div>

          <div className="h-8 ml-4 text-left w-7/12 flex flex-col">
            {flagText && (
              <div className="flex flex-row  mb-4 ">
                <AvailableFlag
                  islogo={false}
                  className="flex items-center justify-center bg-white border-mattamy-blue-light border-2 border-mattamy-blue-light hover:md:bg-mattamy-blue-light
                  mt-1 md:mt-0 hover:md:text-white text-mattamy-blue-light px-2"
                >
                  <p className="px-1 text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1">
                    {flagText}
                  </p>
                </AvailableFlag>
              </div>
            )}

            <CardTitle className="font-trade-gothic-20 text-mattamy-blue text-2xl leading-8 block">
              {communityName}
            </CardTitle>
          </div>
          <div className=" h-auto text-left w-2/12 flex flex-col items-center">
            <button
              className="relative w-10 h-10 lg:w-20 lg:h-20"
              onClick={toggleCardSection}
              aria-label={
                cardOpen
                  ? "Collapse Information"
                  : "Expand for more information"
              }
            >
              <div
                className={cx(
                  "absolute top-0 right-0 left-0 bottom-0 m-auto w-8/12 h-1 bg-action-blue rounded transform transition-transform duration-700",
                  { "rotate-90": !cardOpen }
                )}
              />
              <div className="absolute top-0 right-0 left-0 bottom-0 m-auto w-8/12 h-1 rounded bg-action-blue" />
            </button>
          </div>
        </div>
        <animated.div className="w-full " style={expandCard}>
          {cardOpen && renderCardContent()}
        </animated.div>
      </Card>
    </CardContainer>
  );
};

SaleCenterCommunityCardMobile.propTypes = {
  props: PropTypes.object,
};

export default SaleCenterCommunityCardMobile;
